import React, { useEffect, useState } from 'react';
import { useEnvironment } from '@/hooks/useEnvironment';
import { getHelpInfo } from '@/services/authService';
import { Phone, Mail, MessageSquare, Sparkles } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion';
import { Skeleton } from '@/components/ui/skeleton';
import { useApiState } from '@/hooks/useApiState';
import { useLanguage } from '@/hooks/useLanguage';
import { useNavigate } from 'react-router-dom';

const GetHelpSection = () => {
  const navigate = useNavigate();
  const { isNativeWebView } = useEnvironment();
  const { loading, setLoading, error, setError } = useApiState();
  const [helpInfo, setHelpInfo] = useState(null);
  const { t } = useLanguage();

  // Hardcoded contact information
  const defaultContactInfo = {
    ph: '+91 76809 38741',
    em: 'contact@hansaitechnosoft.com',
    ch: '+91 7680938741',
  };

  useEffect(() => {
    getHelpInfo()
      .then(data => {
        // Merge API data with default contact info (defaults take precedence)
        setHelpInfo({
          ...data,
          ph: defaultContactInfo.ph,
          em: defaultContactInfo.em,
          ch: defaultContactInfo.ch,
        });
        setLoading(false);
      })
      .catch(() => {
        // Use default contact info if API fails
        setHelpInfo({
          ...defaultContactInfo,
          faq: [],
        });
        setLoading(false);
      });
  }, []);

  // Redesigned Web UI
  const WebDesign = () => (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-white py-12 px-2">
      <div className="max-w-4xl mx-auto">
        <div className="flex flex-col items-center mb-10">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-8 h-8 text-orange-400" />
            <h1 className="text-3xl font-bold text-gray-900 tracking-tight">{t('title', 'getHelp')}</h1>
          </div>
          <p className="text-gray-600 text-lg text-center max-w-2xl">
            {t('subtitle', 'getHelp')}
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <Card className="shadow-lg border-0 bg-white hover:shadow-xl transition-shadow">
            <CardContent className="flex flex-col items-center justify-center p-6">
              <Phone className="w-8 h-8 text-blue-600 mb-3" />
              <div className="font-semibold text-gray-800 text-lg">{t('phone', 'getHelp')}</div>
              <a href={`tel:${defaultContactInfo.ph.replace(/\s/g, '')}`} className="text-gray-600 text-base hover:text-blue-600 transition-colors">
                {defaultContactInfo.ph}
              </a>
            </CardContent>
          </Card>
          <Card className="shadow-lg border-0 bg-white hover:shadow-xl transition-shadow">
            <CardContent className="flex flex-col items-center justify-center p-6">
              <Mail className="w-8 h-8 text-green-600 mb-3" />
              <div className="font-semibold text-gray-800 text-lg">{t('email', 'getHelp')}</div>
              <a href={`mailto:${defaultContactInfo.em}`} className="text-gray-600 text-base hover:text-green-600 transition-colors">
                {defaultContactInfo.em}
              </a>
            </CardContent>
          </Card>
          <Card className="shadow-lg border-0 bg-white hover:shadow-xl transition-shadow">
            <CardContent className="flex flex-col items-center justify-center p-6">
              <MessageSquare className="w-8 h-8 text-purple-600 mb-3" />
              <div className="font-semibold text-gray-800 text-lg">{t('chat', 'getHelp')}</div>
              <a href={`https://wa.me/${defaultContactInfo.ch.replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener noreferrer" className="text-gray-600 text-base hover:text-purple-600 transition-colors">
                {defaultContactInfo.ch}
              </a>
            </CardContent>
          </Card>
        </div>
        {helpInfo && helpInfo.faq && helpInfo.faq.length > 0 && (
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="mb-4 flex items-center justify-between gap-3">
              <CardTitle className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <Sparkles className="w-6 h-6 text-orange-400" /> {t('faq', 'getHelp')}
              </CardTitle>
              <Button
                type="button"
                variant="outline"
                className="shrink-0"
                onClick={() => navigate('/faqs')}
              >
                View More
              </Button>
            </div>
            <Accordion type="single" collapsible className="w-full mt-2">
              {helpInfo.faq.map((item, idx) => (
                <AccordionItem key={idx} value={`faq-${idx}`}>
                  <AccordionTrigger className="text-lg font-medium text-gray-800 hover:text-orange-500 transition-colors">Q: {item.q}</AccordionTrigger>
                  <AccordionContent>
                    <span className="text-gray-600 text-base">A: {item.a}</span>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        )}
      </div>
    </div>
  );

  // Mobile UI: iPhone-style, modern, clean, edge-to-edge
  const WebViewDesign = () => (
    <div className="bg-gray-50 min-h-screen pb-4">
      <div className="sticky top-0 z-10 bg-gray-50 flex justify-between items-center px-4 py-3 border-b border-gray-200">
        {/* <div className="font-semibold text-lg text-gray-900">{t('getHelp', 'getHelp')}</div> */}
      </div>
      <div className="space-y-6 px-4 pt-2">
        {/* Contact Info Section */}
        <section className="bg-white rounded-2xl shadow-sm p-4">
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-3">
              <Phone className="w-6 h-6 text-blue-500" />
              <div>
                <div className="font-semibold text-gray-800 text-base">{t('phone', 'getHelp')}</div>
                <a href={`tel:${defaultContactInfo.ph.replace(/\s/g, '')}`} className="text-blue-700 text-base underline">{defaultContactInfo.ph}</a>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Mail className="w-6 h-6 text-blue-500" />
              <div>
                <div className="font-semibold text-gray-800 text-base">{t('email', 'getHelp')}</div>
                <a href={`mailto:${defaultContactInfo.em}`} className="text-blue-700 text-base underline">{defaultContactInfo.em}</a>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <MessageSquare className="w-6 h-6 text-blue-500" />
              <div>
                <div className="font-semibold text-gray-800 text-base">{t('chat', 'getHelp')}</div>
                <a href={`https://wa.me/${defaultContactInfo.ch.replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener noreferrer" className="text-blue-700 text-base underline">{defaultContactInfo.ch}</a>
              </div>
            </div>
          </div>
        </section>
        {/* FAQ Section */}
        {helpInfo && helpInfo.faq && helpInfo.faq.length > 0 && (
          <section className="bg-white rounded-2xl shadow-sm p-4">
            <div className="mb-3 flex items-center justify-between gap-3">
              <div className="font-semibold text-blue-700 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-blue-500" />
                {t('faq', 'getHelp')}
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="shrink-0"
                onClick={() => navigate('/faqs')}
              >
                View More
              </Button>
            </div>
            <Accordion type="single" collapsible className="w-full mt-2">
              {helpInfo.faq.map((item, idx) => (
                <AccordionItem key={idx} value={`faq-mobile-${idx}`}> 
                  <AccordionTrigger className="text-base font-medium text-gray-800 hover:text-blue-500 transition-colors">Q: {item.q}</AccordionTrigger>
                  <AccordionContent>
                    <span className="text-gray-600 text-base">A: {item.a}</span>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>
        )}
      </div>
    </div>
  );

  // Mobile UI Skeleton
  const WebViewSkeleton = () => (
    <div className="p-4">
      <Skeleton className="h-6 w-1/2 mb-4" />
      <div className="bg-white rounded-lg shadow-md p-4 space-y-6">
        {/* Contact Info Skeletons */}
        <div className="space-y-3">
          {[1,2,3].map(i => (
            <div key={i} className="flex items-center space-x-3 p-4 rounded-lg bg-gray-50">
              <Skeleton className="w-5 h-5 rounded-full" />
              <div className="flex-1">
                <Skeleton className="h-4 w-24 mb-1" />
                <Skeleton className="h-4 w-32" />
              </div>
            </div>
          ))}
        </div>
        {/* FAQ Skeletons */}
        <div>
          <Skeleton className="h-5 w-2/3 mb-3" />
          {[1,2,3].map(i => (
            <div key={i} className="mb-2">
              <Skeleton className="h-4 w-full" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  if (loading) {
    if (isNativeWebView) return <WebViewSkeleton />;
    return <div className="p-8 text-center text-gray-600 font-serif">{t('loading', 'common')}</div>;
  }
  if (error) return <div className="p-8 text-center text-red-500 font-serif">{error}</div>;
  return isNativeWebView ? <WebViewDesign /> : <WebDesign />;
};

export { GetHelpSection };